import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react'
import { WatchlistsProvider } from './lib/watchlists-context'
import { ThemeProvider } from './lib/theme-context'
import Layout from './components/Layout'
import LoginShell from './components/LoginShell'
import { RequireAdmin, RequireTool } from './components/RequireAccess'
import Dashboard from './pages/Dashboard'
import WatchMonitorPage from './pages/WatchMonitorPage'
import FinancialAppPage from './pages/FinancialAppPage'
import FamilyFinancePage from './pages/FamilyFinancePage'
import FantasyFootballPage from './pages/FantasyFootballPage'
import FamilyPlannerPage from './pages/FamilyPlannerPage'
import AdminPage from './pages/AdminPage'
import FundamentalsPage from './pages/FundamentalsPage'
import OptionsChainPage from './pages/OptionsChainPage'
import ScreenerPage from './pages/ScreenerPage'
import IdeasPage from './pages/IdeasPage'
import FilingsPage from './pages/FilingsPage'
import ResearchPage from './pages/ResearchPage'
import MarkerLabPage from './pages/MarkerLabPage'
import ConvertiblesPage from './pages/ConvertiblesPage'
import MonteCarloPage from './pages/MonteCarloPage'
import LoginPage from './pages/LoginPage'
import NVenturesPage from './pages/NVenturesPage'
import YahooCallbackPage from './pages/YahooCallbackPage'

// Local-only escape hatch: `npm run dev:bypass` skips Cognito so the UI can be
// worked on offline. import.meta.env.DEV is compile-time false in production
// builds, so this can never activate on the deployed site.
const DEV_AUTH_BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === 'true'

function AuthGate({ children }: { children: React.ReactNode }) {
  const { authStatus } = useAuthenticator((context) => [context.authStatus])

  if (authStatus !== 'authenticated' && !DEV_AUTH_BYPASS) {
    return (
      <LoginShell>
        <Authenticator hideSignUp={true} />
      </LoginShell>
    )
  }
  return <>{children}</>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <Authenticator.Provider>
      <AuthGate>{children}</AuthGate>
    </Authenticator.Provider>
  )
}

export default function App() {
  return (
    <ThemeProvider>
      <WatchlistsProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/fantasy/yahoo/callback"
            element={
              <ProtectedRoute>
                <YahooCallbackPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route
              path="watch-monitor"
              element={
                <RequireTool tool="watch-monitor" label="Watch Monitor">
                  <WatchMonitorPage />
                </RequireTool>
              }
            />
            <Route
              path="financial-app"
              element={
                <RequireTool tool="financial-app" label="Securities Analytics">
                  <FinancialAppPage />
                </RequireTool>
              }
            />
            <Route
              path="financial-app/screener"
              element={
                <RequireTool tool="financial-app" label="Securities Research">
                  <ScreenerPage />
                </RequireTool>
              }
            />
            <Route
              path="financial-app/ideas"
              element={
                <RequireTool tool="financial-app" label="Securities Research">
                  <IdeasPage />
                </RequireTool>
              }
            />
            <Route
              path="financial-app/filings"
              element={
                <RequireTool tool="financial-app" label="Securities Research">
                  <FilingsPage />
                </RequireTool>
              }
            />
            <Route
              path="financial-app/research"
              element={
                <RequireTool tool="financial-app" label="Securities Research">
                  <ResearchPage />
                </RequireTool>
              }
            />
            <Route
              path="financial-app/markers"
              element={
                <RequireTool tool="financial-app" label="Securities Research">
                  <MarkerLabPage />
                </RequireTool>
              }
            />
            <Route
              path="financial-app/convertibles"
              element={
                <RequireTool tool="financial-app" label="Securities Research">
                  <ConvertiblesPage />
                </RequireTool>
              }
            />
            <Route
              path="financial-app/montecarlo"
              element={
                <RequireTool tool="financial-app" label="Securities Research">
                  <MonteCarloPage />
                </RequireTool>
              }
            />
            <Route
              path="financial-app/fundamentals/:symbol"
              element={
                <RequireTool tool="financial-app" label="Securities Analytics">
                  <FundamentalsPage />
                </RequireTool>
              }
            />
            <Route
              path="financial-app/options/:symbol"
              element={
                <RequireTool tool="financial-app" label="Securities Analytics">
                  <OptionsChainPage />
                </RequireTool>
              }
            />
            <Route
              path="family-finance"
              element={
                <RequireTool tool="family-finance" label="Finances">
                  <FamilyFinancePage />
                </RequireTool>
              }
            />
            <Route
              path="fantasy-football"
              element={
                <RequireTool tool="fantasy-football" label="Fantasy Football">
                  <FantasyFootballPage />
                </RequireTool>
              }
            />
            <Route
              path="family-planner"
              element={
                <RequireTool tool="family-planner" label="Family Planner">
                  <FamilyPlannerPage />
                </RequireTool>
              }
            />
            <Route
              path="nventures"
              element={
                <RequireTool tool="nventures" label="NVentures">
                  <NVenturesPage />
                </RequireTool>
              }
            />
            <Route
              path="admin"
              element={
                <RequireAdmin>
                  <AdminPage />
                </RequireAdmin>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </WatchlistsProvider>
    </ThemeProvider>
  )
}
